import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, NgModule, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterModule } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Utilities } from '@app/common/utilities';
import { AppTileHideDialogComponent, AppTileHideDialogModule } from '@app/components/apps-and-tools/dialogs/app-tile-hide-dialog/app-tile-hide-dialog.component';
import { ConfirmDialogComponent, ConfirmDialogModule } from '@app/components/confirm-dialog/confirm-dialog.component';
import { FeatureToggleModule } from '@app/directives/feature-toggle.directive';
import { ImgSrcProxyModule } from '@app/directives/img-src-proxy.directive';
import { AppTile } from '@app/models/app-tile';
import { UserAppTile } from '@app/models/user-app-tile';
import { LinkModule } from '@app/directives/link.directive';
import { AppTileService } from '@app/services/app-tile.service';
import { UserAppTileService } from '@app/services/user-app-tile.service';
import { Environment } from '@environments/interface.environment';
import { isAfter, isSameDay } from 'date-fns';
import { of, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Component({
  selector: 'app-app-tile-box',
  templateUrl: './app-tile-box.component.html',
  styleUrls: ['./app-tile-box.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppTileBoxComponent implements OnInit, OnDestroy {

  @Input() appTile: AppTile;
  @Input() userAppTile: UserAppTile;
  @Input() editMode: boolean;
  @Input() hideTitle: boolean;
  @Input() hideInfo: boolean;
  @Output() removalEvents = new EventEmitter<UserAppTile>(true);
  private subscription: Subscription = new Subscription();

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private dialog: MatDialog,
    private userAppTileService: UserAppTileService,
    private router: Router,
    private appTileService: AppTileService) {
  }

  getInitials(appTile: AppTile) {
    return Utilities.generateInitials(appTile.Title);
  }

  shouldDisplayNew(appTile: AppTile) {
    if (appTile.ShowAsNewUntilThisDate) {
      if (isAfter(appTile.ShowAsNewUntilThisDate, new Date()) || isSameDay(appTile.ShowAsNewUntilThisDate, new Date())) {
        return true;
      }
    }
    return false;
  }

  getBgColor(colorHexCode: string) {
    return Utilities.hexToRGBA(colorHexCode, '0.3');
  }

  getColor(colorHexCode: string) {
    return Utilities.hexToRGBA(colorHexCode, '1');
  }

  getUrl(url: string) {
    return Utilities.getRealUrlFromTokenBasedUrl(this.environment, url);
  }

  remove() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    this.subscription.add(
      dialogRef.afterClosed()
        .pipe(concatMap(result => {
          if (result) {
            return this.userAppTileService.deleteUserAppTile(this.appTile.Id);
          } else {
            return of(null);
          }
        }))
        .subscribe(result => {
          if (result) {
            this.removalEvents.emit(result);
          }
        }));
  }

  hide() {
    const dialogRef = this.dialog.open(AppTileHideDialogComponent);
    this.subscription.add(
      dialogRef.afterClosed()
        .pipe(
          concatMap(result => {
            if (result) {
              this.userAppTile.HideFromUI = true;
              return this.userAppTileService.updateUserAppTile(this.userAppTile.AppTileId, this.userAppTile);
            } else {
              return of(null);
            }
          }))
        .subscribe(result => {
          if (result) {
            this.removalEvents.emit(result);
          }
        }));
  }

  unhide() {
    this.userAppTile.HideFromUI = false;
    this.subscription.add(
      this.userAppTileService.updateUserAppTile(this.userAppTile.AppTileId, this.userAppTile)
        .subscribe(result => {
          if (result) {
            this.appTile = result.AppTile;
          }
        }));
  }

  appClick(event: any) {
    event.preventDefault();
    this.router.navigate(['', { outlets: { modal: ['app-tile-intro', this.appTile.Id] } }]);
  }

  ngOnInit() {
    if (this.appTile && this.appTile.IntegrationEndpointUrl) {
      this.appTile.UnreadCounter$ = this.appTileService.getUnreadCounterFromEndpoint(this.appTile);
    }
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

}
@NgModule({
  declarations: [AppTileBoxComponent],
  exports: [AppTileBoxComponent],
  imports: [
    CommonModule,
    ImgSrcProxyModule,
    FeatureToggleModule,
    MatMenuModule,
    MatBadgeModule,
    MatTooltipModule,
    LinkModule,
    RouterModule,
    ConfirmDialogModule,
    AppTileHideDialogModule
  ]
})
export class AppTileBoxModule { }
